import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import useScrollToTop from "./../../hooks/useScollToTop";
import {
  checkResetPasswordToken,
  resetPassword,
} from "./../../services/passwordServices";
import backgroundImage from "./HeaderSlider_04.webp";

const BRAND = {
  blue: "#013E8E",
  green: "#4CA82F",
  blueLight: "#038DE6",
};

function ResetPasswordPage() {
  useScrollToTop();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [checkingToken, setCheckingToken] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const { token } = useParams();

  // validations simples
  const pwdOk = useMemo(() => password.length >= 10, [password]);
  const matchOk = useMemo(
    () => confirmPassword.length > 0 && password === confirmPassword,
    [password, confirmPassword],
  );

  useEffect(() => {
    const verifyToken = async () => {
      setCheckingToken(true);
      setError("");
      setInfo("");

      try {
        const result = await checkResetPasswordToken(token);
        if (!result?.valid) {
          setError(
            result?.message || "Token de réinitialisation invalide ou expiré.",
          );
        } else {
          setInfo(
            result?.message ||
              "Token valide. Vous pouvez définir un nouveau mot de passe.",
          );
        }
      } catch (e) {
        setError("Impossible de vérifier le token pour le moment.");
      } finally {
        setCheckingToken(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!pwdOk) {
      setError("Mot de passe : minimum 10 caractères.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await resetPassword(token, password);

      if (result?.success) {
        // tu peux garder l'alert si tu veux, mais version plus clean :
        setInfo(result?.message || "Mot de passe réinitialisé avec succès.");
        setTimeout(() => navigate("/login"), 650);
      } else {
        setError(result?.message || "Réinitialisation impossible.");
      }
    } catch (e) {
      setError("Réinitialisation impossible pour le moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full rounded-xl border bg-white px-4 py-3 outline-none transition";
  const focusRing =
    "focus:ring-2 focus:ring-[#013E8E]/25 focus:border-[#013E8E]";
  const inputBorderOk = "border-slate-200";
  const inputBorderErr =
    "border-rose-300 focus:ring-rose-300/30 focus:border-rose-500";

  const disabled = !!error && !info; // token invalide -> on peut désactiver le submit

  return (
    <div
      className="min-h-screen relative flex items-center justify-center px-4 py-10"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* overlay */}
      <div className="absolute inset-0 bg-black/55" />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(circle at top, rgba(1,62,142,0.55), transparent 55%), radial-gradient(circle at bottom, rgba(76,168,47,0.55), transparent 55%)",
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="bg-white/50 backdrop-blur-md border border-white/50 shadow-2xl rounded-2xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src="/img/logos/logo_monappliclub.png"
              alt="MonAppliClub"
              className="h-14 md:h-16 mx-auto mb-3 select-none"
              draggable="false"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
              Réinitialisation
            </h1>
            <p className="text-sm text-slate-700 mt-2">
              Définissez un nouveau mot de passe pour votre compte
            </p>
          </div>

          {/* Etat token */}
          {checkingToken && (
            <div className="mb-5 rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-slate-700 text-sm">
              Vérification du lien de réinitialisation...
            </div>
          )}

          {/* erreurs / infos */}
          {error && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
              {error}
            </div>
          )}
          {!error && info && (
            <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800 text-sm">
              {info}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nouveau mot de passe */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Nouveau mot de passe
              </label>

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`${inputBase} pr-28 ${
                    password.length && !pwdOk ? inputBorderErr : inputBorderOk
                  } ${focusRing}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={checkingToken || disabled}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-3 py-2
                             text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                  aria-label={showPassword ? "Masquer" : "Afficher"}
                  disabled={checkingToken || disabled}
                >
                  {showPassword ? "Masquer" : "Afficher"}
                </button>
              </div>

              <p className="mt-1 text-xs text-slate-600">
                Minimum 10 caractères.
              </p>
            </div>

            {/* Confirmation */}
            <div>
              <label className="block text-sm font-medium text-slate-800 mb-1">
                Confirmez le mot de passe
              </label>

              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  className={`${inputBase} pr-28 ${
                    confirmPassword.length && !matchOk
                      ? inputBorderErr
                      : inputBorderOk
                  } ${focusRing}`}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  disabled={checkingToken || disabled}
                />

                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-3 py-2
                             text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
                  aria-label={showConfirm ? "Masquer" : "Afficher"}
                  disabled={checkingToken || disabled}
                >
                  {showConfirm ? "Masquer" : "Afficher"}
                </button>
              </div>

              {confirmPassword.length > 0 && !matchOk && (
                <p className="mt-1 text-xs text-rose-700">
                  Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={
                checkingToken || disabled || submitting || !pwdOk || !matchOk
              }
              className={`
                w-full rounded-xl px-4 py-3 font-semibold shadow-sm transition border
                ${
                  checkingToken || disabled || submitting || !pwdOk || !matchOk
                    ? "cursor-not-allowed opacity-70"
                    : "hover:opacity-95"
                }
              `}
              style={{
                borderColor: BRAND.blue,
                color: "white",
                backgroundImage: `linear-gradient(90deg, ${BRAND.blue}, ${BRAND.green})`,
              }}
            >
              {submitting
                ? "Réinitialisation..."
                : "Réinitialiser le mot de passe"}
            </button>

            <div className="flex items-center justify-between pt-2 text-sm">
              <Link
                to="/login"
                className="hover:underline"
                style={{ color: BRAND.blue }}
              >
                Retour connexion
              </Link>

              <Link to="/register" className="text-slate-700 hover:underline">
                Créer un compte
              </Link>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-white/85 mt-4">
          MonAppliClub • Réinitialisation sécurisée
        </p>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
